import { CapacitorHttp } from '@capacitor/core';
import axios from 'axios';
import getBaseURL from './utils/constants';
import secureTokenStorage from './utils/secureTokenStorage';

class SecureAPIClient {
  constructor() {
    this.baseURL = getBaseURL();
    this.isNativeApp = secureTokenStorage.isNativePlatform;
    this.isRefreshing = false;
    this.queuedApi = [];
  }

  processQueuedApi(error, token = null) {
    this.queuedApi.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.queuedApi = [];
  }

  async refreshAccessToken() {
    try {
      const refreshToken = await secureTokenStorage.getRefreshToken();
      
      // Prepare request data
      const refreshData = this.isNativeApp && refreshToken 
        ? { refresh_token: refreshToken } 
        : {};

      let response;
      
      if (this.isNativeApp) {
        // Native: Use CapacitorHttp
        response = await CapacitorHttp.post({
          url: `${this.baseURL}/auth/refresh`,
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-Platform': 'capacitor'
          },
          data: refreshData
        });
        // Normalize response structure
        response = { data: response.data };
      } else {
        // Web: Use axios (cookies handled automatically)
        response = await axios.post(`${this.baseURL}/auth/refresh`, refreshData);
      }

      const res = response.data;
      
      if (res && res.s && res.d && res.d.access_token) {
        // Store new tokens securely
        await secureTokenStorage.setTokens(
          res.d.access_token, 
          res.d.refresh_token // Will be undefined for web (stays in cookie)
        );
        
        // Update localStorage with inst_id and scholar_id if provided
        if (res.d.inst_id) {
          localStorage.setItem('inst_id', res.d.inst_id);
        }
        if (res.d.scholar_id) {
          localStorage.setItem('scholar_id', res.d.scholar_id);
        }
        
        return res.d.access_token;
      }
      
      throw new Error('Invalid refresh response');
    } catch (error) {
      // Refresh failed - logout user
      await secureTokenStorage.clearTokens();
      throw error;
    }
  }

  async request(api, data = {}, method = 'POST') {
    const url = `${this.baseURL}${api}`;
    const accessToken = await secureTokenStorage.getAccessToken();

    const headers = {
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Add platform identifier for backend to reliably detect Capacitor apps
    if (this.isNativeApp) {
      headers['X-Client-Platform'] = 'capacitor';
    }

    try {
      let response;

      if (this.isNativeApp) {
        // Native: Use CapacitorHttp
        response = await CapacitorHttp.request({
          url,
          method,
          headers,
          data: method !== 'GET' ? data : undefined
        });
        // Normalize response structure
        response = { data: response.data };
      } else {
        // Web: Use axios
        if (method === 'POST') {
          response = await axios.post(url, data, { headers });
        } else if (method === 'GET') {
          response = await axios.get(url, { headers });
        }
      }

      const res = response.data;

      // Check for token expiration (error code 1003)
      if (res && !res.s && res.f === 1003) {
        // Token expired - need to refresh
        if (this.isRefreshing) {
          // Already refreshing, queue this request
          return new Promise((resolve, reject) => {
            this.queuedApi.push({ resolve, reject });
          }).then(() => {
            // Retry with new token
            return this.request(api, data, method);
          });
        }

        this.isRefreshing = true;

        try {
          const newToken = await this.refreshAccessToken();

          this.processQueuedApi(null, newToken);
          this.isRefreshing = false;
          
          // Retry original request with new token
          return this.request(api, data, method);
        } catch (refreshError) {
          this.processQueuedApi(refreshError, null);
          this.isRefreshing = false;
          
          // Redirect to login
          await secureTokenStorage.clearTokens();
          window.location.href = '/sign-in';
          throw refreshError;
        }
      }

      // Check for session invalidation (error code 1006)
      if (res && !res.s && res.f === 1006) {
        await secureTokenStorage.clearTokens();
        window.location.href = '/sign-in';
        throw new Error('Session expired');
      }

      return res;
    } catch (error) {
      // Network or other errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Unauthorized - clear tokens and redirect
        await secureTokenStorage.clearTokens();
        window.location.href = '/sign-in';
      }
      throw error;
    }
  }

  async post(api, data = {}) {
    return this.request(api, data, 'POST');
  }

  async get(api) {
    return this.request(api, {}, 'GET');
  }
}

const secureApi = new SecureAPIClient();

export default secureApi;
